import { useCallback, useEffect, useState } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useToast } from '../../Helpers/ToastHelper';
import { Nullable } from '../../Common/Imports';
import { ApiResponse } from '../../Models/Classes';
import { ChildContainerProps } from '../../Models/Template';
import { MailFolder, Message } from '@microsoft/microsoft-graph-types';
import { MailContext } from './MailContextValue';

export type ExtendedMailFolder = MailFolder & {
    children?: ExtendedMailFolder[];
};

export type ExtendedMessage = Message & {
    Files?: Nullable<FileList>;
};

const isValidEmailAddress = (email?: string | null): boolean => {
    return !!email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
};

const normalizeRecipients = (recipients: Message['toRecipients'] = []) => {
    return (recipients ?? [])
        .map((recipient) => ({
            ...recipient,
            emailAddress: {
                ...recipient.emailAddress,
                address: recipient.emailAddress?.address?.trim() ?? ''
            }
        }))
        .filter((recipient) => recipient.emailAddress.address);
};

export const MailProvider = (props: ChildContainerProps) => {
    const MailBox = "reservationcheckhv@stayconfirmed.com";
    const [mails, setMails] = useState<Message[] | undefined>();
    const [folders, setFolders] = useState<ExtendedMailFolder[]>([]);
    const AxiosHelper = useAxios();
    const { showToast, handleToastError } = useToast();
    const [tableIsLoading, setTableIsLoading] = useState<boolean>(false);
    const [folderListIsLoading, setFolderListIsLoading] = useState<boolean>(false);
    const [selectedFolder, setSelectedFolder] = useState<ExtendedMailFolder | undefined>();
    const [selectedMail, setSelectedMail] = useState<Message | undefined>();

    const findFolderById = useCallback((folders: ExtendedMailFolder[], id: string): ExtendedMailFolder | undefined => {
        for (const folder of folders) {
            if (folder.id === id) {
                return folder;
            }

            // If folder has children, recursively search them
            if (folder.children && folder.children.length > 0) {
                const found = findFolderById(folder.children, id);
                if (found) {
                    return found;
                }
            }
        }
        return undefined; // Return undefined if no folder is found
    }, []);

    const GetMessages = useCallback(async (FolderId: string, elements: number = 150) => {
        setTableIsLoading(true);
        setMails(undefined); // Clear current messages to reflect loading state
        const _T = findFolderById(folders, FolderId);
        setSelectedFolder(_T);
        try {
            const resp = await AxiosHelper.get(`/Email/GetMessages/${MailBox}/${FolderId}/${elements}`);
            if (resp.data.code === 0) {
                const fetchedMessages = resp.data.data.map((msg: Message) => ({
                    ...msg,
                    checkIn: msg.receivedDateTime ? new Date(msg.receivedDateTime) : null,
                }));
                setMails(fetchedMessages);
            }
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setTableIsLoading(false);
        }
    }, [AxiosHelper, findFolderById, folders]);

    // Fetch folders
    const GetData = useCallback(async () => {
        setFolderListIsLoading(true);
        try {
            const resp = await AxiosHelper.get(`/Email/GetFolders/${MailBox}`);
            if (resp.data.code === 0) {
                setFolders(resp.data.data);
            }
        } catch (error) {
            console.error('Error fetching folders:', error);
        } finally {
            setFolderListIsLoading(false);
        }
    }, [AxiosHelper]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    // Updated GetChildrens function
    const GetChildrens = async (folder: ExtendedMailFolder) => {
        try {
            const resp = await AxiosHelper.get<ApiResponse<MailFolder[]>>(`/Email/GetChildrenFolders/${MailBox}/${folder.id}`);
            const childFolders: ExtendedMailFolder[] = resp.data.data.map(m => ({
                ...m,
                children: [] // Initialize empty children array
            }));

            // Recursively update the correct folder in the hierarchy
            const updatedFolders = updateFolderChildren(folders, folder.id as string, childFolders);
            setFolders(updatedFolders); // Update state with the new folders
        } catch (error) {
            console.error('Error fetching child folders:', error);
        }
    };

    // Recursive function to update folders in the hierarchy
    const updateFolderChildren = (folders: ExtendedMailFolder[], folderId: string, children: ExtendedMailFolder[]): ExtendedMailFolder[] => {
        return folders.map(folder => {
            if (folder.id === folderId) {
                // Update the current folder with its new children
                return { ...folder, children };
            } else if (folder.children && folder.children.length > 0) {
                // Recursively update children folders
                return { ...folder, children: updateFolderChildren(folder.children, folderId, children) };
            }
            return folder;
        });
    };

    const onArchive = (id: string) => {
        return id;
    };

    const onDelete = async (id: string) => {
        try {
            const response = await AxiosHelper.post<ApiResponse<boolean>>(`Email/DeleteEmail/${MailBox}/${id}`);

            showToast(
                response.data.code === 1 ? 'success' : 'error',
                response.data.code === 1 ? 'Mail deleted successfully' : response.data.message || 'Failed to deleted mail'
            );
            if (selectedFolder)
                GetMessages(selectedFolder.id as string);

        } catch (error: unknown) {
            handleToastError(error);
        }
    };

    const onSend = async (mail: ExtendedMessage, addinfo1?: Nullable<string>): Promise<boolean> => {
        try {
            const toRecipients = normalizeRecipients(mail.toRecipients);
            const ccRecipients = normalizeRecipients(mail.ccRecipients);
            const invalidRecipient = [...toRecipients, ...ccRecipients].find(
                recipient => !isValidEmailAddress(recipient.emailAddress?.address)
            );

            if (toRecipients.length === 0) {
                showToast('warn', 'Missing recipient', 'Please add at least one valid recipient.');
                return false;
            }

            if (invalidRecipient) {
                showToast('warn', 'Invalid recipient', invalidRecipient.emailAddress?.address);
                return false;
            }

            if (!mail.subject?.trim()) {
                showToast('warn', 'Missing subject', 'Please add a subject before sending.');
                return false;
            }

            if (!mail.body?.content?.trim()) {
                showToast('warn', 'Missing body', 'Please add a message before sending.');
                return false;
            }

            const normalizedMail: ExtendedMessage = {
                ...mail,
                from: mail.from ?? { emailAddress: { address: MailBox } },
                toRecipients,
                ccRecipients,
                body: {
                    ...mail.body,
                    content: mail.body.content,
                    contentType: mail.body.contentType ?? 'html'
                }
            };
            const { Files, ...messageToSend } = normalizedMail;
            const formData = new FormData();

            // Append the JSON string of the message
            formData.append("message", JSON.stringify(messageToSend));

            // Append each file as an attachment
            if (Files && Files.length > 0) {
                Array.from(Files).forEach((file) => {
                    formData.append(`attachments`, file, file.name);
                });
            }

            const response = await AxiosHelper.post<ApiResponse<boolean>>(
                `Operative/SendEmail/${MailBox}${addinfo1 ? `?${addinfo1}` : ""}`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            const isSuccess = response.data.code === 1;
            showToast(
                isSuccess ? 'success' : 'error',
                isSuccess ? 'Mail sent successfully' : response.data.message || response.data.exception || 'Failed to send mail'
            );

            return isSuccess;

        } catch (error: unknown) {
            handleToastError(error);
            return false;
        }
    };




    const value = {
        MailBox,
        mails,
        folders,
        selectedFolder,
        onArchive,
        onDelete,
        onSend,
        GetChildrens,
        GetMessages,
        tableIsLoading,
        folderListIsLoading,
        setSelectedMail,
        selectedMail
    };


    return (
        <MailContext.Provider value={value}>
            {props.children}
        </MailContext.Provider>
    );
};
